import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import { BUTTON_VARIANTS, INPUT_TYPES } from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useAddSeriesModalViewModel } from "./useAddSeriesModalViewModel";

const schema = z.object({
  name: z.string().min(1, "Series Name is required"),
  specLevel: z.string().optional(),
  accountId: z.string().uuid(),
});

export function AddSeriesModal({
  open,
  closeModal,
  selectedSeries,
  setSelectedSeries,
  setRefresh,
  accountId,
}) {
  const {
    formState: { isValid, isDirty },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      specLevel: "",
      accountId,
      seriesGroupId: null,
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (selectedSeries?.id) {
      reset(selectedSeries);
    } else {
      reset({ name: "", specLevel: "", accountId, seriesGroupId: null });
    }
  }, [selectedSeries, accountId]);

  const handleCloseModal = () => {
    setSelectedSeries(null);
    closeModal();
    reset();
  };

  const {
    isLoading,
    debouncedSearch,
    specLevelOptions,
    seriesGroupOptions,
    getSeriesGroupOptions,
    setSearch,
    getSpecLevelOptions,
    handleCreateSeries,
    handleUpdateSeries,
  } = useAddSeriesModalViewModel({
    accountId,
    setRefresh,
    handleCloseModal,
  });

  const { classes } = useStyles();
  const { specLevel } = getValues();

  useEffect(() => {
    if (open) {
      getSeriesGroupOptions();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      getSpecLevelOptions();
    }
  }, [open, debouncedSearch]);

  const seriesFields = [
    {
      label: "Series Name",
      name: "name",
      formControl: control,
      required: true,
      inputProps: { maxLength: 128 },
    },
    {
      label: "Spec Level",
      name: "specLevel",
      formControl: control,
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      options: specLevelOptions,
      freeSolo: true,
      disableClearable: true,
      value: specLevel,
      maxLength: 128,
      textFieldOnChange: (e) => setSearch(e.target.value),
      getOptionLabel: (option) => option,
      renderOption: (props, option) => (
        <Box {...props} key={option} className={classes.optionListContainer}>
          <Typography
            className={classes.optionList}
            onClick={() =>
              setValue("specLevel", option, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          >
            {option}
          </Typography>
        </Box>
      ),
    },
    {
      label: "Series Group",
      name: "seriesGroupId",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: seriesGroupOptions,
      style: { display: seriesGroupOptions.length > 1 ? "block" : "none" },
    },
  ];

  const handleSave = () => {
    handleCreateSeries(getValues());
  };

  const handleUpdate = () => {
    handleUpdateSeries(getValues());
  };

  return (
    <MiModal
      open={open}
      closeModal={handleCloseModal}
      loading={isLoading}
      title={`${selectedSeries?.id ? "Edit" : "Add"} Series`}
      description={`${
        selectedSeries?.id ? "Edit" : "Enter"
      } series details below.`}
      actions={[
        {
          name: "Cancel",
          onClick: handleCloseModal,
        },
        {
          name: selectedSeries?.id ? "Update Series" : "Save Series",
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: selectedSeries?.id ? handleUpdate : handleSave,
          disabled: !isValid || !isDirty,
        },
      ]}
    >
      <MiDetailFields detailFields={seriesFields} />
    </MiModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  optionListContainer: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.grey.light,
    },
  },
  optionList: {
    padding: theme.spacing(1),
  },
}));
